/**
 * FBTR range sliders for the collection filters (price per metre, roll length).
 *
 * Each slider sits over the hidden checkboxes of the filter's values. When a handle is released we tick the values
 * inside the range and fire an `input` event, which the theme's own filter form (collection-filters-form.js) turns into
 * a request and a re-render. The slider reads its position from the server-rendered markup, so it survives re-renders.
 * Also keeps a count of active filters on the Filter button, with each slider counted once.
 */
class FbtrRangeFacet extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;
    // Dragging fires `input` many times: keep those away from the theme's form handler
    this.addEventListener('input', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.type === 'range') {
        event.stopPropagation();
        this.#move(event.target);
      }
    });
    this.addEventListener('change', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.type === 'range') this.#apply();
    });
  }

  get lo() {
    return this.querySelector('[data-lo]');
  }

  get hi() {
    return this.querySelector('[data-hi]');
  }

  #fmt(n) {
    return this.dataset.unit === 'rand' ? `R ${n}` : `${n} m`;
  }

  #move(which) {
    const { lo, hi } = this;
    if (+lo.value > +hi.value) {
      if (which === lo) lo.value = hi.value;
      else hi.value = lo.value;
    }
    const min = +lo.min, max = +lo.max, span = max - min || 1, a = +lo.value, b = +hi.value;
    const fill = this.querySelector('.fbtr-range__fill');
    fill.style.left = `${((a - min) / span) * 100}%`;
    fill.style.right = `${100 - ((b - min) / span) * 100}%`;
    this.querySelector('[data-lo-label]').textContent = this.#fmt(a);
    this.querySelector('[data-hi-label]').textContent = this.#fmt(b) + (this.dataset.unit === 'rand' ? ' / m' : '');
  }

  #apply() {
    const { lo, hi } = this;
    const a = +lo.value, b = +hi.value, full = a <= +lo.min && b >= +lo.max;
    // Roll lengths are whole metres: 48 covers 48.0 to 48.9
    const top = b + (this.dataset.unit === 'metre' ? 0.999 : 0);
    const boxes = [...this.querySelectorAll('input[type=checkbox]')];
    boxes.forEach((box) => {
      const n = parseFloat(box.dataset.num);
      box.checked = !full && n >= a && n <= top;
    });
    // The theme listens for `input` on the form, and uses the event's group to leave this slider in place
    boxes[0]?.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

if (!customElements.get('fbtr-range-facet')) customElements.define('fbtr-range-facet', FbtrRangeFacet);

/** Number of active filters (a slider counts once) on the Filter button */
(() => {
  const update = () => {
    const chips = document.querySelectorAll('.active-facets-desktop .active-facets__button:not(.btn--clear--all)').length;
    document.querySelectorAll('.filter_button').forEach((button) => {
      let badge = button.querySelector('.fbtr-filter-count');
      if (!chips) return badge?.remove();
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'fbtr-filter-count';
        button.append(badge);
      }
      badge.textContent = String(chips);
    });
  };
  const start = () => {
    const chips = document.querySelector('.active-facets-desktop');
    if (!chips) return;
    new MutationObserver(update).observe(chips, { childList: true });
    update();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
